// hooks/useRichTextStyles.js
import { useEffect } from 'react';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { createRoot } from 'react-dom/client';

export const useRichTextStyles = () => {
  useEffect(() => {
    // Add carousel scroll function to window
    window.scrollCarousel = (carouselId, direction) => {
      const carousel = document.getElementById(carouselId);
      if (carousel) {
        const scrollAmount = 270; // Approximate width of one item plus gap
        carousel.scrollBy({
          left: direction * scrollAmount,
          behavior: 'smooth'
        });
      }
    };

    // Add copy buttons to existing code blocks
    const addCopyButtonsToCodeBlocks = () => {
      const codeBlocks = document.querySelectorAll('.rich-text-content pre');

      codeBlocks.forEach(codeBlock => {
        // Check if copy button already exists (check parent for the wrapper)
        if (codeBlock.parentElement?.classList.contains('code-block-wrapper')) {
          return;
        }

        // Create wrapper div for the code block and button
        const wrapper = document.createElement('div');
        wrapper.className = 'code-block-wrapper';
        wrapper.style.position = 'relative';
        wrapper.style.margin = '16px 50px';

        // Insert wrapper before the code block
        codeBlock.parentNode.insertBefore(wrapper, codeBlock);
        
        // Move the code block into the wrapper and reset its margin
        wrapper.appendChild(codeBlock);
        codeBlock.style.margin = '0';

        // Create copy button
        const copyButton = document.createElement('button');
        copyButton.className = 'copy-code-btn';
        copyButton.setAttribute('aria-label', 'Copy code to clipboard');

        // Create containers for icons
        const copyIconContainer = document.createElement('span');
        copyIconContainer.className = 'copy-icon';

        const checkIconContainer = document.createElement('span');
        checkIconContainer.className = 'check-icon';
        checkIconContainer.style.display = 'none'; // Initially hide the check icon

        // Render Ant Design icons using React
        const copyRoot = createRoot(copyIconContainer);
        copyRoot.render(<CopyOutlined />);

        const checkRoot = createRoot(checkIconContainer);
        checkRoot.render(<CheckOutlined />);

        // Add icons to button
        copyButton.appendChild(copyIconContainer);
        copyButton.appendChild(checkIconContainer);

        // Add click handler - use the global copy function
        copyButton.addEventListener('click', async (e) => {
          e.preventDefault();
          const codeElement = codeBlock.querySelector('code') || codeBlock;

          // Pass the specific button and icon elements to the global copyCodeBlock function
          if (window.copyCodeBlock) {
            await window.copyCodeBlock(codeElement, copyButton, copyIconContainer, checkIconContainer);
          } else {
            console.error('Global copyCodeBlock function not available');
            // Fallback for direct copy if global function isn't set up
            try {
              const codeToCopy = codeElement.innerText;
              await navigator.clipboard.writeText(codeToCopy);
              // Directly handle the animation here if global function is missing
              copyIconContainer.style.display = 'none';
              checkIconContainer.style.display = 'inline'; // Use inline here as well
              setTimeout(() => {
                copyIconContainer.style.display = 'inline'; // Use inline here as well
                checkIconContainer.style.display = 'none';
              }, 2000); // Show checkmark for 2 seconds
            } catch (err) {
              console.error('Failed to copy code: ', err);
            }
          }
        });

        // Insert the button into the wrapper (outside the code block)
        wrapper.appendChild(copyButton);
      });
    };

    // Run initially
    addCopyButtonsToCodeBlocks();

    // Also run when new content is added (for dynamic content)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const newCodeBlocks = node.querySelectorAll && node.querySelectorAll('.rich-text-content pre');
              if (newCodeBlocks && newCodeBlocks.length > 0) {
                setTimeout(addCopyButtonsToCodeBlocks, 50); // A small delay to ensure DOM is ready
              }
            }
          });
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Inject rich text styles into the document head
    const styleId = 'rich-text-styles-global';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* Quill editor alignment classes */
        .rich-text-content .ql-align-center {
          text-align: center !important;
        }

        .rich-text-content .ql-align-right {
          text-align: right !important;
        }

        .rich-text-content .ql-align-justify {
          text-align: justify !important;
        }

        /* Quill editor indentation classes */
        .rich-text-content .ql-indent-1 {
          padding-left: 3em !important;
        }

        .rich-text-content .ql-indent-2 {
          padding-left: 6em !important;
        }

        .rich-text-content .ql-indent-3 {
          padding-left: 9em !important;
        }

        .rich-text-content .ql-indent-4 {
          padding-left: 12em !important;
        }

        .rich-text-content .ql-indent-5 {
          padding-left: 15em !important;
        }

        .rich-text-content .ql-indent-6 {
          padding-left: 18em !important;
        }

        .rich-text-content .ql-indent-7 {
          padding-left: 21em !important;
        }

        .rich-text-content .ql-indent-8 {
          padding-left: 24em !important;
        }

        /* Enhanced typography */
        .rich-text-content h1 {
          font-size: 2.25em !important;
          font-weight: 700 !important;
          margin: 1.5em 0 0.5em 0 !important;
          color: #1f2937 !important;
        }

        .rich-text-content h2 {
          font-size: 1.875em !important;
          font-weight: 600 !important;
          margin: 1.3em 0 0.5em 0 !important;
          color: #1f2937 !important;
        }

        .rich-text-content h3 {
          font-size: 1.5em !important;
          font-weight: 600 !important;
          margin: 1.2em 0 0.5em 0 !important;
          color: #1f2937 !important;
        }

        .rich-text-content h4 {
          font-size: 1.25em !important;
          font-weight: 600 !important;
          margin: 1.1em 0 0.5em 0 !important;
          color: #1f2937 !important;
        }

        .rich-text-content h5 {
          font-size: 1.125em !important;
          font-weight: 600 !important;
          margin: 1em 0 0.5em 0 !important;
          color: #1f2937 !important;
        }

        .rich-text-content h6 {
          font-size: 1em !important;
          font-weight: 600 !important;
          margin: 1em 0 0.5em 0 !important;
          color: #1f2937 !important;
        }

        .rich-text-content p {
          margin: 1em 0 !important;
          line-height: 1.7 !important;
        }

        .rich-text-content ol {
          list-style-type: decimal !important;
          margin: 1em 0 !important;
          padding-left: 2em !important;
        }

        .rich-text-content ul {
          list-style-type: disc !important;
          margin: 1em 0 !important;
          padding-left: 2em !important;
        }

        .rich-text-content li {
          margin: 0.5em 0 !important;
          line-height: 1.6 !important;
          display: list-item !important;
        }

        .rich-text-content ol ol {
          list-style-type: lower-alpha !important;
        }

        .rich-text-content ol ol ol {
          list-style-type: lower-roman !important;
        }

        .rich-text-content ul ul {
          list-style-type: circle !important;
        }

        .rich-text-content ul ul ul {
          list-style-type: square !important;
        }

        .rich-text-content blockquote {
          margin: 1.5em 0 !important;
          padding: 1em 1.5em !important;
          border-left: 4px solid #9333ea !important;
          background-color: #faf5ff !important;
          font-style: italic !important;
          color: #6b21a8 !important;
        }

        .rich-text-content blockquote p {
          margin: 0 !important;
        }

        .rich-text-content code {
          background-color: #f1f5f9 !important;
          padding: 0.125em 0.25em !important;
          border-radius: 0.25em !important;
          font-family: 'Courier New', monospace !important;
          font-size: 0.875em !important;
          color: #dc2626 !important;
        }

        /* Code Block Wrapper - Contains both the code block and copy button */
        .rich-text-content .code-block-wrapper {
          position: relative !important;
          margin: 16px 50px !important;
        }

        /* Code Block Styling - Matches Editor Preview */
        .rich-text-content .code-block-wrapper pre {
          background-color: #f8f9fa !important;
          border: 1px solid #e9ecef !important;
          border-radius: 8px !important;
          padding: 16px !important;
          margin: 0 !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: 14px !important;
          line-height: 1.5 !important;
          overflow-x: auto !important;
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
  
        }

        /* Copy button styling - Now positioned outside the code block */
        .rich-text-content .code-block-wrapper .copy-code-btn {
          position: absolute !important;
          top: -18px !important;
          left: 0px !important;
          background: rgba(0,0,0,0.7) !important;
          color: rgba(255,255,255,0.9) !important;
          border: 1px solid rgba(255,255,255,0.2) !important;
          border-radius: 4px !important;
          padding: 8px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          z-index: 10 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          min-width: 32px !important;
          height: 32px !important;
          backdrop-filter: blur(4px) !important;
        }

        .rich-text-content .code-block-wrapper .copy-code-btn:hover {
          background: rgba(0,0,0,0.9) !important;
          color: rgba(255,255,255,1) !important;
          transform: scale(1.05) !important;
          border-color: rgba(255,255,255,0.4) !important;
        }

        .rich-text-content .code-block-wrapper .copy-code-btn:active {
          transform: scale(0.95) !important;
        }

        .rich-text-content .code-block-wrapper .copy-code-btn svg {
          width: 16px !important;
          height: 16px !important;
          display: block !important;
        }

        /* Adjust language badge position when copy button is present */
        .rich-text-content .code-block-wrapper pre[data-language]:not([data-language=""])::before {
          right: 50px !important;
        }

        /* For code blocks without language, position copy button normally */
        .rich-text-content .code-block-wrapper pre:not([data-language]) ~ .copy-code-btn,
        .rich-text-content .code-block-wrapper pre[data-language=""] ~ .copy-code-btn {
          right: 8px !important;
        }

        /* Language-specific themes for code blocks */
        .rich-text-content .code-block-wrapper pre[data-language="javascript"] {
          background-color: #2d3748 !important;
          color: #f7df1e !important;
          border-color: #4a5568 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="typescript"] {
          background-color: #f0f8ff !important;
          color: #3178c6 !important;
          border-color: #3178c6 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="jsx"],
        .rich-text-content .code-block-wrapper pre[data-language="tsx"] {
          background-color: #282c34 !important;
          color: #61dafb !important;
          border-color: #61dafb !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="python"] {
          background-color: #ffffcc !important;
          color: #306998 !important;
          border-color: #306998 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="java"] {
          background-color: #fff5ee !important;
          color: #ed8b00 !important;
          border-color: #ed8b00 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="html"] {
          background-color: #fff5f5 !important;
          color: #e34c26 !important;
          border-color: #e34c26 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="css"] {
          background-color: #f0f8ff !important;
          color: #1572b6 !important;
          border-color: #1572b6 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="php"] {
          background-color: #f8f5ff !important;
          color: #777bb4 !important;
          border-color: #777bb4 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="sql"] {
          background-color: #f0f8ff !important;
          color: #336791 !important;
          border-color: #336791 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="bash"] {
          background-color: #f5fff5 !important;
          color: #4eaa25 !important;
          border-color: #4eaa25 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="json"] {
          background-color: #f6f8fa !important;
          color: #000000 !important;
          border-color: #d0d7de !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="ruby"] {
          background-color: #fff0f0 !important;
          color: #cc342d !important;
          border-color: #cc342d !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="go"] {
          background-color: #f0f8ff !important;
          color: #00add8 !important;
          border-color: #00add8 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="rust"] {
          background-color: #fff5f0 !important;
          color: #ce422b !important;
          border-color: #ce422b !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="swift"] {
          background-color: #fff8f0 !important;
          color: #fa7343 !important;
          border-color: #fa7343 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="kotlin"] {
          background-color: #f0f0ff !important;
          color: #7f52ff !important;
          border-color: #7f52ff !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="dart"] {
          background-color: #f0f8ff !important;
          color: #0175c2 !important;
          border-color: #0175c2 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="scss"] {
          background-color: #fff0f8 !important;
          color: #cf649a !important;
          border-color: #cf649a !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="xml"] {
          background-color: #fff5f0 !important;
          color: #e65100 !important;
          border-color: #e65100 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="yaml"] {
          background-color: #f8f8ff !important;
          color: #6b46c1 !important;
          border-color: #6b46c1 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="text"] {
          background-color: #f9f9f9 !important;
          color: #333333 !important;
          border-color: #cccccc !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="csharp"] {
          background-color: #f5f0ff !important;
          color: #239120 !important;
          border-color: #239120 !important;
        }

        .rich-text-content .code-block-wrapper pre[data-language="cpp"],
        .rich-text-content .code-block-wrapper pre[data-language="c"] {
          background-color: #f0f5ff !important;
          color: #00599c !important;
          border-color: #00599c !important;
        }

        /* Language badge in top-right corner */
        .rich-text-content .code-block-wrapper pre[data-language]:not([data-language=""])::before {
          content: attr(data-language) !important;
          position: absolute !important;
          top: 8px !important;
          right: 12px !important;
          font-size: 11px !important;
          color: rgba(255,255,255,0.8) !important;
          text-transform: uppercase !important;
          font-weight: bold !important;
          letter-spacing: 0.5px !important;
          background: rgba(0,0,0,0.2) !important;
          padding: 4px 8px !important;
          border-radius: 4px !important;
          line-height: 1 !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
        }

        /* Remove conflicting code styling for code blocks */
        .rich-text-content .code-block-wrapper pre code {
          background-color: transparent !important;
          color: inherit !important;
          padding: 0 !important;
          border-radius: 0 !important;
          font-size: inherit !important;
        }

        /* Fallback for any pre tags not wrapped (maintain original styling) */
        .rich-text-content pre:not(.code-block-wrapper pre) {
          background-color: #f8f9fa !important;
          border: 1px solid #e9ecef !important;
          border-radius: 8px !important;
          padding: 16px !important;
          margin: 16px 50px !important;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
          font-size: 14px !important;
          line-height: 1.5 !important;
          overflow-x: auto !important;
          position: relative !important;
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
        }

        .rich-text-content strong {
          font-weight: 700 !important;
        }

        .rich-text-content em {
          font-style: italic !important;
        }

        .rich-text-content u {
          text-decoration: underline !important;
        }

        .rich-text-content s {
          text-decoration: line-through !important;
        }

        .rich-text-content a {
          color: #9333ea !important;
          text-decoration: underline !important;
        }

        .rich-text-content a:hover {
          color: #7c3aed !important;
        }

        .rich-text-content table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin: 1.5em 0 !important;
        }

        .rich-text-content th, .rich-text-content td {
          border: 1px solid #d1d5db !important;
          padding: 0.75em !important;
          text-align: left !important;
        }

        .rich-text-content th {
          background-color: #f9fafb !important;
          font-weight: 600 !important;
        }

        .rich-text-content hr {
          border: none !important;
          height: 1px !important;
          background-color: #d1d5db !important;
          margin: 2em 0 !important;
        }

        /* Media container styles for regular block content */
        .rich-text-content .media-container {
          margin: 1.5em 0 !important;
          padding: 1em !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 0.5em !important;
          background-color: #f9fafb !important;
        }

        .rich-text-content .media-container img {
          display: block !important;
          margin: 0 auto !important;
          border-radius: 0.5em !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
          transition: transform 0.2s ease !important;
        }

        .rich-text-content .media-container img:hover {
          transform: scale(1.02) !important;
        }

        .rich-text-content .media-container video {
          display: block !important;
          margin: 0 auto !important;
          border-radius: 0.5em !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }

        .rich-text-content .media-container iframe {
          border-radius: 0.5em !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }

        /* List media horizontal layout - using nowrap and overflow like original */
        .rich-text-content .list-media-horizontal {
          display: flex !important;
          flex-wrap: nowrap !important;
          gap: 0.5em !important;
          margin: 0.5em 0 !important;
          padding: 0 !important;
          border: none !important;
          border-radius: 0 !important;
          background-color: transparent !important;
          list-style: none !important;
          overflow-x: auto !important;
          scroll-behavior: smooth !important;
          scrollbar-width: none !important; /* Hide scrollbar for Firefox */
          -ms-overflow-style: none !important; /* Hide scrollbar for IE and Edge */
          justify-content: flex-start !important; /* Default left alignment */
        }

        /* Alignment overrides for media containers */
        .rich-text-content .list-media-horizontal.ql-align-center,
        .rich-text-content .media-carousel-container.ql-align-center {
          justify-content: center !important;
        }

        .rich-text-content .list-media-horizontal.ql-align-center {
          justify-content: center !important;
        }

        .rich-text-content .list-media-horizontal.ql-align-right,
        .rich-text-content .media-carousel-container.ql-align-right {
          justify-content: flex-end !important;
        }

        .rich-text-content .list-media-horizontal.ql-align-right {
          justify-content: flex-end !important;
        }

        .rich-text-content .list-media-horizontal.ql-align-justify {
          justify-content: space-between !important;
        }

        /* Center the entire carousel container when center-aligned */
        .rich-text-content .media-carousel-container.ql-align-center {
          display: flex !important;
          justify-content: center !important;
        }

        .rich-text-content .media-carousel-container.ql-align-right {
          display: flex !important;
          justify-content: flex-end !important;
        }

        /* Hide scrollbar for WebKit browsers */
        .rich-text-content .list-media-horizontal::-webkit-scrollbar {
          display: none !important;
        }

        .rich-text-content .media-carousel-container {
          position: relative !important;
          margin: 0.5em 0 !important;
        }

        .rich-text-content .media-carousel-nav {
          position: absolute !important;
          top: 50% !important;
          transform: translateY(-50%) !important;
          background: rgba(255, 255, 255, 0.9) !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 50% !important;
          width: 40px !important;
          height: 40px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          cursor: pointer !important;
          z-index: 10 !important;
          transition: all 0.2s ease !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        }

        .rich-text-content .media-carousel-nav:hover {
          background: rgba(147, 51, 234, 0.1) !important;
          border-color: #9333ea !important;
          transform: translateY(-50%) scale(1.1) !important;
        }

        .rich-text-content .media-carousel-nav.prev {
          left: -20px !important;
        }

        .rich-text-content .media-carousel-nav.next {
          right: -20px !important;
        }

        .rich-text-content .media-carousel-nav svg {
          width: 16px !important;
          height: 16px !important;
          color: #9333ea !important;
        }

        .rich-text-content .list-media-item {
          flex: 0 0 250px !important;
          min-width: 250px !important;
          max-width: 350px !important;
        }

        .rich-text-content .list-media-item img {
          width: 100% !important;
          height: auto !important;
          max-height: 300px !important;
          object-fit: contain !important;
          border-radius: 0.5em !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
          transition: transform 0.2s ease !important;
          cursor: pointer !important;
        }

        .rich-text-content .list-media-item img:hover {
          transform: scale(1.02) !important;
        }

        .rich-text-content .list-media-item video {
          width: 100% !important;
          height: auto !important;
          max-height: 300px !important;
          object-fit: contain !important;
          border-radius: 0.5em !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }

        /* List items with media should have no list styling */
        .rich-text-content li.has-media {
          list-style: none !important;
          margin-left: -2em !important;
          padding-left: 0 !important;
        }

        /* Responsive design */
        @media (max-width: 768px) {
          .rich-text-content .ql-indent-1 {
            padding-left: 1.5em !important;
          }

          .rich-text-content .ql-indent-2 {
            padding-left: 3em !important;
          }

          .rich-text-content .ql-indent-3 {
            padding-left: 4.5em !important;
          }

          .rich-text-content .ql-indent-4 {
            padding-left: 6em !important;
          }

          .rich-text-content .ql-indent-5 {
            padding-left: 7.5em !important;
          }

          .rich-text-content .ql-indent-6 {
            padding-left: 9em !important;
          }

          .rich-text-content .ql-indent-7 {
            padding-left: 10.5em !important;
          }

          .rich-text-content .ql-indent-8 {
            padding-left: 12em !important;
          }

          .rich-text-content .list-media-item {
            flex: 0 0 200px !important;
            min-width: 200px !important;
            max-width: 250px !important;
          }

          .rich-text-content .list-media-item img,
          .rich-text-content .list-media-item video {
            max-height: 200px !important;
          }

          .rich-text-content .media-carousel-nav {
            width: 32px !important;
            height: 32px !important;
          }

          .rich-text-content .media-carousel-nav.prev {
            left: -16px !important;
          }

          .rich-text-content .media-carousel-nav.next {
            right: -16px !important;
          }

          .rich-text-content .media-carousel-nav svg {
            width: 14px !important;
            height: 14px !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // Cleanup function
    return () => {
      observer.disconnect();
      // Optionally remove the style tag on unmount if it's not needed globally
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);
};